import type React from 'react';
import { Flex, Box, Text, ScrollArea, Separator } from '@radix-ui/themes';
import {
  DotFilledIcon,
  TransformIcon,
  MagnifyingGlassIcon,
  BlendingModeIcon,
  ClockIcon,
  ComponentInstanceIcon,
  PlayIcon,
  StackIcon,
  ActivityLogIcon,
} from '@radix-ui/react-icons';

interface NodeItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  type: string;
  description: string;
}

interface NodeCategory {
  title: string;
  // icon: React.ReactNode
  nodes: NodeItem[];
}

const nodeCategories: NodeCategory[] = [
  {
    title: 'Data',
    // icon: <ActivityLogIcon width={16} height={16} />,
    nodes: [
      {
        id: 'signal',
        label: 'Signal',
        icon: <DotFilledIcon width={12} height={12} />,
        type: 'SignalNode',
        description: 'Start workflow with raw data from an on-chain event',
      },
      {
        id: 'transformer',
        label: 'Transformer',
        icon: <TransformIcon width={12} height={12} />,
        type: 'TransformerNode',
        description: 'Modify, restructure, or compute new values',
      },
      {
        id: 'enricher',
        label: 'Enricher',
        icon: <MagnifyingGlassIcon width={12} height={12} />,
        type: 'EnricherNode',
        description: 'Fetch data from external sources to enrich workflow',
      },
    ],
  },
  {
    title: 'Logic & Flow Control',
    // icon: <BlendingModeIcon width={16} height={16} />,
    nodes: [
      {
        id: 'filter',
        label: 'Filter',
        icon: <BlendingModeIcon width={12} height={12} />,
        type: 'FilterNode',
        description: 'Direct workflow paths based on IF/THEN/ELSE logic',
      },
      // {
      //   id: "delay",
      //   label: "Delay",
      //   icon: <ClockIcon width={12} height={12} />,
      //   type: "delay",
      //   description: "Pause workflow for specific duration or until set time",
      // },
      // {
      //   id: "merge",
      //   label: "Merge",
      //   icon: <ComponentInstanceIcon width={12} height={12} />,
      //   type: "merge",
      //   description: "Combine multiple workflow branches into single path",
      // },
    ],
  },
  {
    title: 'Execution',
    // icon: <PlayIcon width={16} height={16} />,
    nodes: [
      {
        id: 'action',
        label: 'Action',
        icon: <PlayIcon width={12} height={12} />,
        type: 'ActionNode',
        description: 'Perform final task that creates external side effects',
      },
      // {
      //   id: "sub-workflow",
      //   label: "Sub-Workflow",
      //   icon: <StackIcon width={12} height={12} />,
      //   type: "sub-workflow",
      //   description: "Trigger and run another separate workflow",
      // },
    ],
  },
];

const NodePalette: React.FC = () => {
  const onDragStart = (
    event: React.DragEvent,
    nodeType: string,
    nodeVariant: string,
  ) => {
    console.log('nodeType', nodeType);
    console.log('nodeVariant', nodeVariant);
    event.dataTransfer.setData(
      'application/reactflow',
      `${nodeType}:${nodeVariant}`,
    );
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <Box
      width="320px"
      height="100vh"
      style={{
        backgroundColor: 'var(--gray-1)',
        borderRight: '1px solid var(--gray-6)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        px="4"
        py="3"
        style={{
          borderBottom: '1px solid var(--gray-6)',
          backgroundColor: 'var(--gray-2)',
        }}
      >
        <Flex align="center" gap="2">
          <ActivityLogIcon width={16} height={16} />
          <Text size="3" weight="bold" color="gray">
            Workflow Nodes
          </Text>
        </Flex>
      </Box>

      <ScrollArea style={{ flex: 1 }}>
        <Flex direction="column" width="100%" px="3" py="3" gap="6">
          {nodeCategories.map((category, categoryIndex) => (
            <Box key={category.title} width="100%">
              {/* Category Header */}
              <Flex align="center" gap="2" mb="2">
                {/* <span style={{ color: "var(--accent-9)", display: "flex", alignItems: "center" }}>
                  {category.icon}
                </span> */}
                <Text size="2" weight="bold" color="gray">
                  {category.title}
                </Text>
              </Flex>

              {/* Category Nodes */}
              <Flex direction="column" gap="2" width="100%">
                {category.nodes.map((node) => (
                  <Box
                    key={node.id}
                    draggable
                    onDragStart={(event) =>
                      onDragStart(event, node.type, 'default')
                    }
                    width="100%"
                    style={{
                      backgroundColor: 'var(--gray-2)',
                      border: '1px solid var(--gray-6)',
                      borderRadius: '8px',
                      cursor: 'grab',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      padding: '10px 12px',
                      boxSizing: 'border-box',
                      maxWidth: '100%',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                      e.currentTarget.style.borderColor = 'var(--gray-7)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow =
                        '0 2px 6px var(--gray-a6)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--gray-2)';
                      e.currentTarget.style.borderColor = 'var(--gray-6)';
                      e.currentTarget.style.transform = 'translateY(0px)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.cursor = 'grabbing';
                      e.currentTarget.style.transform = 'translateY(0px)';
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.cursor = 'grab';
                    }}
                  >
                    {/* Node Header */}
                    <Flex align="center" gap="2">
                      <span
                        style={{
                          color: 'var(--accent-10)',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'var(--accent-3)',
                          padding: '4px',
                          borderRadius: '4px',
                        }}
                      >
                        {node.icon}
                      </span>
                      <Text size="2" weight="bold" color="gray">
                        {node.label}
                      </Text>
                    </Flex>
                    {/* Node Description */}
                    <Text
                      size="1"
                      color="gray"
                      mt="1"
                      style={{ lineHeight: 1.3 }}
                    >
                      {node.description}
                    </Text>
                  </Box>
                ))}
              </Flex>

              {/* Separator between categories */}
              {/* {categoryIndex < nodeCategories.length - 1 && (
                <Separator my="4" size="4" color="gray" />
              )} */}
            </Box>
          ))}
        </Flex>
      </ScrollArea>
    </Box>
  );
};

export default NodePalette;
